import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { isEmpty } from 'lodash';
import { MethodsOfContact } from 'src/components/Facesheet/Profile/ContactRequirements/components';
import { DrawerContext } from 'src/components/ClientsDrawer/context/DrawerContext';
import { EMPTY_MEMBER_CONTACT_VALUE, MEMBER_CONTACT_EDIT_LABEL } from './memberContact.constants';
import { getMemberContactSectionData } from './memberContact.utils';

const FIELD_LABEL_CLASS = 'mb-0.5 text-sm leading-5 font-bold text-medium-grey';

function Field({
  actionDisabled,
  actionLabel,
  className,
  label,
  onActionClick,
  value,
}) {
  return (
    <div className={`min-w-0 border-b border-solid border-light-border-blue py-4 ${className}`}>
      <div className="mb-1 flex items-start justify-between gap-4">
        <div className={FIELD_LABEL_CLASS}>
          {label}
        </div>
        <button
          aria-label={`${actionLabel} ${label}`}
          className={`shrink-0 border-0 bg-transparent p-0 font-regular-font text-sm leading-5 ${
            actionDisabled ?
              'cursor-not-allowed text-medium-grey opacity-50' :
              'cursor-pointer text-action-blue hover:underline'
          }`}
          disabled={actionDisabled}
          onClick={onActionClick}
          type="button"
        >
          {actionLabel}
        </button>
      </div>
      <div className="font-regular-font text-sm leading-5 text-brand-blue whitespace-pre-wrap break-words">
        {value}
      </div>
    </div>
  );
}

Field.propTypes = {
  actionDisabled: PropTypes.bool,
  actionLabel: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  onActionClick: PropTypes.func,
  value: PropTypes.node.isRequired,
};

Field.defaultProps = {
  actionDisabled: false,
  actionLabel: 'Edit',
  className: '',
  onActionClick: undefined,
};

export function MemberContactSection({
  contact,
}) {
  const drawerContext = useContext(DrawerContext);
  const openDrawer = drawerContext?.openDrawer;

  if (isEmpty(contact)) {
    return null;
  }

  const {
    address,
    preferredContactMethods,
    preferredLanguage,
  } = getMemberContactSectionData(contact);

  return (
    <div className="px-5">
      <div className="grid grid-cols-2 gap-x-10">
        <div className="flex flex-col">
          <Field
            actionDisabled={!openDrawer}
            actionLabel={MEMBER_CONTACT_EDIT_LABEL}
            label="Address"
            onActionClick={openDrawer ? () => openDrawer({
              highlightProfileTarget: true,
              initialTab: 'profile',
              profileScrollTarget: 'edit-address-btn',
            }) : undefined}
            value={address}
          />
          <Field
            actionDisabled={!openDrawer}
            actionLabel={MEMBER_CONTACT_EDIT_LABEL}
            className="border-b-0"
            label="Preferred Contact Method"
            onActionClick={openDrawer ? () => openDrawer({
              highlightProfileTarget: true,
              initialTab: 'profile',
              profileScrollTarget: 'methods-of-contact-edit-btn',
            }) : undefined}
            value={
              preferredContactMethods.length > 0 ?
                <MethodsOfContact contactMethods={preferredContactMethods} /> :
                EMPTY_MEMBER_CONTACT_VALUE
            }
          />
        </div>

        <div className="flex flex-col">
          <Field
            actionDisabled={!openDrawer}
            actionLabel={MEMBER_CONTACT_EDIT_LABEL}
            className="border-b-0"
            label="Preferred Language"
            onActionClick={openDrawer ? () => openDrawer({
              highlightProfileTarget: true,
              initialTab: 'profile',
              profileScrollTarget: 'preferred-languages-edit-btn',
            }) : undefined}
            value={preferredLanguage}
          />
        </div>
      </div>
    </div>
  );
}

MemberContactSection.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.string,
    addresses: PropTypes.array,
    preferred_languages: PropTypes.array,
    contact_preference: PropTypes.shape({
      preferred_contact_methods: PropTypes.array,
      timeslots: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
      ]),
      contact_notes: PropTypes.string,
    }),
  }),
};

MemberContactSection.defaultProps = {
  contact: null,
};

export default MemberContactSection;
